/**
 * P1.1: Ontology Versions Manager Component
 * 
 * Manages ontology versioning:
 * - View versions (released, staging)
 * - Create snapshots
 * - Release versions
 * - Set active version
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { 
  GitBranch,
  Package,
  Plus,
  RefreshCw,
  CheckCircle,
  Clock,
  Tag,
  History,
  ArrowRight,
  Rocket,
  Database,
  Star
} from 'lucide-react';
import { toast } from 'sonner';
import { adminFetchHeaders } from '@/hooks/useAdminFetch';

const API = window.location.origin;

const getAuthHeaders = () => adminFetchHeaders(true);

// Bump type configuration
const BUMP_TYPES = {
  MAJOR: { 
    label: 'MAJOR', 
    description: 'Breaking Changes (ID-Rewire, Semantik)',
    color: 'bg-red-100 text-red-800'
  },
  MINOR: { 
    label: 'MINOR', 
    description: 'Neue Konzepte/Relations (backwards compatible)',
    color: 'bg-blue-100 text-blue-800'
  },
  PATCH: { 
    label: 'PATCH', 
    description: 'Bugfixes (Typos, Metadata)',
    color: 'bg-green-100 text-green-800'
  }
};

const OntologyVersionsManager = () => {
  const [versions, setVersions] = useState([]);
  const [stagingData, setStagingData] = useState(null);
  const [activeVersion, setActiveVersion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedExam, setSelectedExam] = useState('PSM1');
  
  // Create snapshot dialog
  const [showCreateSnapshot, setShowCreateSnapshot] = useState(false);
  const [newSnapshot, setNewSnapshot] = useState({
    exam_ids: ['PSM1'],
    bump_type: 'MINOR',
    release_notes: '',
    auto_release: false
  });
  
  // Available exams
  const exams = ['PSM1', 'PSM2', 'PSPO1', 'SAFe', 'ITIL', 'PRINCE2', 'PMP', 'CAPM'];

  // Fetch versions
  const fetchVersions = useCallback(async () => {
    try {
      const response = await fetch(`${API}/api/ontology-versions?exam_id=${selectedExam}`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setVersions(data);
      }
    } catch (error) {
      console.error('Error fetching versions:', error);
    }
  }, [selectedExam]);

  // Fetch staging
  const fetchStaging = useCallback(async () => {
    try {
      const response = await fetch(`${API}/api/ontology-versions/staging/${selectedExam}`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setStagingData(data);
      }
    } catch (error) {
      console.error('Error fetching staging:', error);
    }
  }, [selectedExam]);

  // Fetch active version
  const fetchActiveVersion = useCallback(async () => {
    try {
      const response = await fetch(`${API}/api/ontology-versions/active/${selectedExam}`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setActiveVersion(data);
      }
    } catch (error) {
      console.error('Error fetching active version:', error);
    }
  }, [selectedExam]);

  // Load data
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchVersions(), fetchStaging(), fetchActiveVersion()]);
      setLoading(false);
    };
    loadData();
  }, [fetchVersions, fetchStaging, fetchActiveVersion]);

  // Create snapshot
  const handleCreateSnapshot = async () => {
    try {
      const response = await fetch(`${API}/api/ontology-versions/create-snapshot`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(newSnapshot)
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Version ${data.version_number} erstellt${newSnapshot.auto_release ? ' und released' : ''}`);
        setShowCreateSnapshot(false);
        setNewSnapshot({
          exam_ids: [selectedExam],
          bump_type: 'MINOR',
          release_notes: '',
          auto_release: false
        });
        await Promise.all([fetchVersions(), fetchStaging(), fetchActiveVersion()]);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Fehler beim Erstellen');
      }
    } catch (error) {
      toast.error('Fehler: ' + error.message);
    }
  };

  // Release version
  const handleRelease = async (versionId) => {
    try {
      const response = await fetch(`${API}/api/ontology-versions/${versionId}/release?set_active=true`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Version ${data.version_number} released und aktiviert`);
        await Promise.all([fetchVersions(), fetchActiveVersion()]);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Fehler');
      }
    } catch (error) {
      toast.error('Fehler: ' + error.message);
    }
  };

  // Set active
  const handleSetActive = async (versionId) => {
    try {
      const response = await fetch(`${API}/api/ontology-versions/${versionId}/set-active?exam_id=${selectedExam}`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        toast.success('Aktive Version geändert');
        await fetchActiveVersion();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Fehler');
      }
    } catch (error) {
      toast.error('Fehler: ' + error.message);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="w-6 h-6 animate-spin text-purple-500" />
        <span className="ml-2">Lade Versions...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <GitBranch className="w-6 h-6 text-purple-600" />
            Ontology Versioning
          </h2>
          <p className="text-gray-600 mt-1">
            Semantic Versioning mit immutablen Snapshots (P1.1)
          </p>
        </div>
        <div className="flex gap-2">
          <Select value={selectedExam} onValueChange={setSelectedExam}>
            <SelectTrigger className="w-[150px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {exams.map(exam => (
                <SelectItem key={exam} value={exam}>{exam}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            onClick={() => Promise.all([fetchVersions(), fetchStaging(), fetchActiveVersion()])}
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
          <Button onClick={() => setShowCreateSnapshot(true)} data-testid="create-snapshot-btn">
            <Plus className="w-4 h-4 mr-2" />
            Snapshot erstellen
          </Button>
        </div>
      </div>

      {/* Active Version Card */}
      {activeVersion && !activeVersion.admin_hint && (
        <Card className="border-2 border-green-500 bg-green-50">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Star className="w-6 h-6 text-green-600" />
                <div>
                  <div className="font-bold text-green-800">
                    Aktive Version: {activeVersion.version_number}
                  </div>
                  <div className="text-sm text-green-600">
                    {activeVersion.node_count} Nodes, {activeVersion.edge_count} Edges
                  </div>
                </div>
              </div>
              <div className="text-sm text-green-600">
                Released: {activeVersion.released_at ? new Date(activeVersion.released_at).toLocaleString('de-DE') : '-'}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      
      {activeVersion?.admin_hint && (
        <Card className="border-yellow-300 bg-yellow-50">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-yellow-700">
              <Clock className="w-5 h-5" />
              <span>{activeVersion.admin_hint}</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Staging Graph */}
      {stagingData && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Database className="w-5 h-5 text-blue-600" />
              Staging Graph ({selectedExam})
            </CardTitle>
            <CardDescription>
              Genehmigte Konzepte, die noch nicht released wurden
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="p-3 bg-gray-50 rounded">
                <div className="text-2xl font-bold">{stagingData.node_count}</div>
                <div className="text-sm text-gray-600">Staged Nodes</div>
              </div>
              <div className="p-3 bg-gray-50 rounded">
                <div className="text-2xl font-bold">{stagingData.edge_count}</div>
                <div className="text-sm text-gray-600">Staged Edges</div>
              </div>
              <div className="p-3 bg-gray-50 rounded">
                <div className="text-2xl font-bold">
                  {Object.keys(stagingData.redirect_mappings || {}).length}
                </div>
                <div className="text-sm text-gray-600">Redirects</div>
              </div>
            </div>
            
            {stagingData.staged_nodes?.length > 0 && (
              <div className="border rounded p-3">
                <div className="text-sm font-medium mb-2">Staged Nodes:</div>
                <div className="flex flex-wrap gap-2">
                  {stagingData.staged_nodes.slice(0, 10).map((node) => (
                    <Badge key={node.node_id} variant="outline">
                      {node.canonical_name}
                    </Badge>
                  ))}
                  {stagingData.staged_nodes.length > 10 && (
                    <Badge variant="secondary">
                      +{stagingData.staged_nodes.length - 10} mehr
                    </Badge>
                  )}
                </div>
              </div>
            )}
            
            {stagingData.last_version && (
              <div className="mt-4 text-sm text-gray-500">
                Letzte Version: {stagingData.last_version.version_number}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Versions List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="w-5 h-5 text-purple-600" />
            Versionen ({versions.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {versions.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <GitBranch className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Keine Versionen vorhanden.</p>
              <p className="text-sm mt-2">Erstellen Sie den ersten Snapshot.</p>
            </div>
          ) : (
            <div className="space-y-3 max-h-[500px] overflow-y-auto pr-2" data-testid="versions-list">
              {versions.map((version) => (
                <div 
                  key={version.version_id}
                  className={`border rounded-lg p-4 ${version.is_active ? 'border-green-500 bg-green-50' : ''}`}
                  data-testid={`version-${version.version_id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Tag className="w-5 h-5 text-purple-600" />
                      <div>
                        <div className="font-bold text-lg">{version.version_number}</div>
                        <div className="text-sm text-gray-600">
                          {version.node_count} Nodes, {version.edge_count} Edges
                        </div>
                      </div>
                      
                      <Badge className={BUMP_TYPES[version.bump_type]?.color || ''}>
                        {version.bump_type}
                      </Badge>
                      
                      {version.is_released && (
                        <Badge className="bg-green-100 text-green-800">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Released
                        </Badge>
                      )}
                      
                      {version.is_active && (
                        <Badge className="bg-purple-100 text-purple-800">
                          <Star className="w-3 h-3 mr-1" />
                          Aktiv
                        </Badge>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {!version.is_released && (
                        <Button
                          size="sm"
                          onClick={() => handleRelease(version.version_id)}
                        >
                          <Rocket className="w-4 h-4 mr-1" />
                          Release
                        </Button>
                      )}
                      
                      {version.is_released && !version.is_active && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleSetActive(version.version_id)}
                        >
                          <Star className="w-4 h-4 mr-1" />
                          Aktivieren
                        </Button>
                      )}
                    </div>
                  </div>
                  
                  {version.release_notes && (
                    <div className="mt-2 text-sm text-gray-600 border-t pt-2">
                      {version.release_notes}
                    </div>
                  )}
                  
                  <div className="mt-2 text-xs text-gray-400">
                    Erstellt: {new Date(version.created_at).toLocaleString('de-DE')} von {version.created_by}
                    {version.released_at && (
                      <span className="ml-4">
                        Released: {new Date(version.released_at).toLocaleString('de-DE')}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Snapshot Dialog */}
      <Dialog open={showCreateSnapshot} onOpenChange={setShowCreateSnapshot}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="w-5 h-5 text-purple-600" />
              Ontology Snapshot erstellen
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Exams</Label>
              <Select 
                value={newSnapshot.exam_ids[0]} 
                onValueChange={(v) => setNewSnapshot({...newSnapshot, exam_ids: [v]})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {exams.map(exam => (
                    <SelectItem key={exam} value={exam}>{exam}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Version Bump</Label>
              <div className="space-y-2 mt-2">
                {Object.entries(BUMP_TYPES).map(([key, config]) => (
                  <div 
                    key={key}
                    className={`border rounded p-3 cursor-pointer ${
                      newSnapshot.bump_type === key 
                        ? 'border-purple-500 bg-purple-50' 
                        : 'hover:bg-gray-50'
                    }`}
                    onClick={() => setNewSnapshot({...newSnapshot, bump_type: key})}
                  >
                    <div className="flex items-center gap-2">
                      <Badge className={config.color}>{config.label}</Badge>
                      <span className="text-sm text-gray-600">{config.description}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div>
              <Label>Release Notes</Label>
              <Textarea
                value={newSnapshot.release_notes}
                onChange={(e) => setNewSnapshot({...newSnapshot, release_notes: e.target.value})}
                placeholder="Was ist in dieser Version enthalten?"
                rows={3}
              />
            </div>
            
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="auto-release"
                checked={newSnapshot.auto_release}
                onChange={(e) => setNewSnapshot({...newSnapshot, auto_release: e.target.checked})}
                className="rounded"
              />
              <Label htmlFor="auto-release" className="cursor-pointer">
                Sofort releasen und aktivieren
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateSnapshot(false)}>
              Abbrechen
            </Button>
            <Button onClick={handleCreateSnapshot}>
              <Package className="w-4 h-4 mr-2" />
              Snapshot erstellen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OntologyVersionsManager;
